import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "@mui/material";
import LendyIcon from "../../components/lendy/LendyIcon";
import AssistantAvatar from "../AssistantAvatar";
import { markIntroSeen, markProfileSeen, reportInbox, useAgentNews } from "../agentNews";
import IdentityTab from "./IdentityTab";
import LendyIntro from "./LendyIntro";
import ScheduleTab from "./ScheduleTab";
import { getAutopilotState } from "./autopilotFormat";
import useAutopilot from "./useAutopilot";
import "../../components/lendy/lendyShared.css";
import "./lendyProfile.css";

// Approvals and Activity are hidden for now (ApprovalsTab and ActivityTab are kept to bring them
// back); apps and MCP servers have their own page (apps/AppsPage).
const TABS = [
  { id: "identity", label: "Identity", icon: "fingerprint" },
  { id: "schedule", label: "Scheduled tasks", icon: "calendar" },
];

const toTab = (id) => (TABS.some((tab) => tab.id === id) ? id : TABS[0].id);

/**
 * The assistant's profile (Ask Addy v2's Autopilot, after the lendy-ui design boards 24-29): its
 * instructions and memory, and its scheduled tasks.
 */
const LendyProfile = ({
  agentId,
  assistantName,
  assistantImageUrl,
  identity,
  isPanel = false,
  side = "right",
  isCloseInRail = false,
  initialTab = "identity",
  onTabChange,
  onClose,
  onUnreadChange,
}) => {
  const [tab, setTab] = useState(toTab(initialTab));
  const [isIntroOpen, setIsIntroOpen] = useState(false);
  const isLeftPanel = isPanel && side === "left";
  const autopilot = useAutopilot(identity);
  const { inbox, jobs, error, isConnected, reload } = autopilot;
  const state = getAutopilotState(autopilot);

  // The sidebar opens the profile on a tab, also while it is already open.
  useEffect(() => {
    setTab(toTab(initialTab));
  }, [initialTab]);

  const { isIntroNew } = useAgentNews();

  // What is unread (the header reports it too while the profile is closed). With the profile open,
  // whatever comes in is seen.
  useEffect(() => {
    if (inbox) {
      onUnreadChange?.(inbox.unread || 0);
      reportInbox(inbox);
    }
    markProfileSeen();
  }, [inbox, onUnreadChange]);

  const openTab = (id) => {
    setTab(id);
    onTabChange?.(id);
  };
  const closeIcon = isLeftPanel ? "panelLeftClose" : isPanel ? "panelRightClose" : "left";
  const closeLabel = isPanel ? `Close ${assistantName}'s profile` : "Back to chat";
  // Wide sidebar: the profile sits beside the chat, in a panel; narrow: it covers the chat.
  const frame = (content) =>
    isPanel ? <div className={`lendy-profile-panel${isLeftPanel ? " lendy-profile-panel--left" : ""}`}>{content}</div> : content;
  // The intro covers the whole sidebar, not just the profile's panel.
  const intro =
    isIntroOpen &&
    createPortal(
      <div className="lendy-intro-screen">
        <LendyIntro assistantName={assistantName} assistantImageUrl={assistantImageUrl} onBack={() => setIsIntroOpen(false)} />
      </div>,
      document.body
    );

  return frame(
    <>
      {intro}
      <section className="lendy-profile" aria-label={`${assistantName}'s profile`}>
        {/* Beside the rail, the rail's panel toggle closes it. */}
        {!isCloseInRail && (
          <div className="lendy-profile-top">
            <button type="button" className="lendy-float-button" aria-label={closeLabel} title={isPanel ? "Close" : "Back to chat"} onClick={onClose}>
              <LendyIcon name={closeIcon} />
            </button>
          </div>
        )}

        <div className={`lendy-profile-hero${isCloseInRail ? " lendy-profile-hero--top" : ""}`}>
          <span className="lendy-profile-face">
            <AssistantAvatar imageUrl={assistantImageUrl} size="profile" />
            <button
              type="button"
              className={`lendy-intro-dot${isIntroNew ? " lendy-ping" : ""}`}
              aria-label={`Say hi to ${assistantName}: what it can do`}
              title={`What ${assistantName} can do`}
              onClick={() => {
                markIntroSeen();
                setIsIntroOpen(true);
              }}
            >
              <i aria-hidden="true" />
            </button>
          </span>
          <h2 className="lendy-profile-name">{assistantName}</h2>
          {isConnected && state.text && (
            <p className={`lendy-profile-state lendy-profile-state--${state.tone}`}>
              <i aria-hidden="true" />
              {state.text}
            </p>
          )}
        </div>

        <div className="lendy-profile-tabs" role="tablist" aria-label={`${assistantName}'s profile`}>
          {TABS.map(({ id, label, icon }) => (
            <Tooltip key={id} title={label} arrow>
              <button type="button" role="tab" aria-selected={tab === id} aria-label={label} onClick={() => openTab(id)}>
                <LendyIcon name={icon} size={18} />
              </button>
            </Tooltip>
          ))}
        </div>

        <div className="lendy-profile-pane" role="tabpanel">
          {!isConnected && <p className="lendy-profile-note">Scheduled tasks aren't connected in this build, so there's nothing to show yet.</p>}
          {isConnected && tab === "schedule" && (
            <ScheduleTab jobs={jobs} error={error} identity={autopilot.identity} loanId={identity.loanId} assistantName={assistantName} onChanged={reload} />
          )}
          {isConnected && tab === "identity" && <IdentityTab identity={autopilot.identity} agentId={agentId} assistantName={assistantName} error={error} onChanged={reload} />}
        </div>
      </section>
    </>
  );
};

export default LendyProfile;
